import { z } from "zod";
import type { BindingResponse } from "../types";

const MAX_NAME_LENGTH = 256;
const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_INSTRUCTIONS_LENGTH = 5000;
const MAX_SLUG_LENGTH = 256;
const MAX_ALLOWED_PASSTHROUGH_HEADERS = 100;
const MAX_HEADER_NAME_LENGTH = 256;
const MAX_CONSENT_SKIP_CLIENT_IDS = 64;
const MAX_CONSENT_SKIP_CLIENT_ID_LENGTH = 2048;

// RFC 7230 header-name token grammar (visible ASCII, excluding delimiters).
export const HEADER_NAME_TOKEN_REGEX = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

export type McpServerAuthType = "arcade" | "arcade_header" | "user_source";

type McpDiscoveryConfig = {
  enabled: boolean;
};

type McpToolFilter = {
  allowed_tools: string[];
  discovery?: McpDiscoveryConfig;
};

const mcpToolFilterSchema = z.object({
  allowed_tools: z.array(z.string()),
  discovery: z.object({ enabled: z.boolean() }).optional(),
});

const consentSkipClientIdsSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1, "Client ID cannot be empty")
      .max(MAX_CONSENT_SKIP_CLIENT_ID_LENGTH, "Client ID too long")
  )
  .max(MAX_CONSENT_SKIP_CLIENT_IDS, "Too many client IDs")
  .refine((ids) => new Set(ids).size === ids.length, "Duplicate client IDs")
  .optional();

const allowedPassthroughHeadersSchema = z
  .array(
    z
      .string()
      .max(MAX_HEADER_NAME_LENGTH, "Header name too long")
      .regex(
        HEADER_NAME_TOKEN_REGEX,
        "Invalid header name — use visible ASCII characters only (no spaces or special delimiters)"
      )
  )
  .max(MAX_ALLOWED_PASSTHROUGH_HEADERS, "Too many passthrough headers")
  .optional();

const mcpServerAuthTypeSchema = z.enum([
  "arcade",
  "arcade_header",
  "user_source",
]);

export const mcpServerCreateSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "Name is required")
      .max(MAX_NAME_LENGTH, "Name is too long"),
    description: z
      .string()
      .max(MAX_DESCRIPTION_LENGTH, "Description is too long")
      .optional(),
    instructions: z
      .string()
      .max(MAX_INSTRUCTIONS_LENGTH, "Instructions are too long")
      .optional(),
    slug: z
      .string()
      .max(MAX_SLUG_LENGTH, "Slug is too long")
      .refine((val) => !val.toLowerCase().includes("arcade"), {
        message: 'Slug cannot contain "arcade". This word is reserved.',
      })
      .optional()
      .nullable(),
    status: z.enum(["active", "inactive"]).optional(),
    auth_type: mcpServerAuthTypeSchema.optional(),
    user_source_id: z.string().optional().nullable(),
    tool_filter: mcpToolFilterSchema.optional(),
    allowed_passthrough_headers: allowedPassthroughHeadersSchema,
    consent_skip_client_ids: consentSkipClientIdsSchema,
  })
  .superRefine((val, ctx) => {
    if (val.auth_type === "user_source" && !val.user_source_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select a User Source",
        path: ["user_source_id"],
      });
    }
  });
export type McpServerCreateRequest = z.infer<typeof mcpServerCreateSchema>;

export type McpServerUpdateRequest = McpServerCreateRequest;

export type McpServerPatchRequest = Partial<McpServerUpdateRequest>;

type McpServerStatus = "active" | "inactive";

export type McpServer = {
  id: string;
  name: string;
  description: string;
  instructions: string;
  slug: string;
  status: McpServerStatus;
  auth_type: McpServerAuthType;
  user_source_id: string | null;
  tool_filter: McpToolFilter;
  allowed_passthrough_headers: string[];
  consent_skip_client_ids?: string[];
  binding: BindingResponse;
  created_at: string;
  updated_at: string;
};

const mcpSlugCheckSchema = z.object({
  slug: z.string().min(1, "Slug is required"),
});
export type McpSlugCheckRequest = z.infer<typeof mcpSlugCheckSchema>;

export type McpSlugCheckResponse = {
  available: boolean;
};
